import { useEffect, useState } from 'react';

import { useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import copy from 'copy-to-clipboard';
import {
    IconCopy,
    IconTrash,
} from '@tabler/icons-react';

import { TableContainer } from '../../Styles/CustomViews/Pages/ListComponent/customListComponentStyles';
import { InstanceDetailsContainer, SPTableContainer } from '../../Styles/InstanceManagement/instanceManagementStyles';

import LocalStorageHelper from '../../Utilities/Helpers/LocalStorageHelper';
import QueryBuilderHelper from '../../Utilities/Helpers/QueryBuilderHelper';
import DateHelper from '../../Utilities/Helpers/DateHelper';

import { CachingEnum } from '../../Utilities/Enums/CachingEnum';
import { OrderByEnum } from '../../Utilities/Enums/SortEnums';
import { DBInstancesListField } from '../../Utilities/Enums/ParametersEnum';
import { DBInstancesStatusEnum, DBInstancesStatusTextEnum, InstanceDataModeEnum, InstanceDataModeTextEnum, InstanceQueryTypeEnum } from '../../Utilities/Enums/StatusEnums';

import CustomListComponent from '../../CustomViews/Pages/ListComponent/CustomListComponent';
import CustomIconButton from '../../CustomViews/Elements/CustomIconButton';
import CustomDeleteConfirmation from '../../CustomViews/Elements/CustomDeleteConfirmation/CustomDeleteConfirmation';

import { DBInstancesList, DBInstanceInterface } from '../../Models/InstanceManagement/InstanceManagement';

import { useInstanceManagementHook } from '../../Hooks/InstanceManagement/instanceManagementHooks';

export default function DBInstances(props: DBInstanceInterface) {
    let { triggeredQuery, settriggeredQuery } = props;
    let location = useLocation();
    let queryParams = new URLSearchParams(location.search);
    let instanceId = queryParams.get('id') ?? "0";
    let isDarkMode = LocalStorageHelper.isDarkMode();
    const { enqueueSnackbar } = useSnackbar();

    // states
    const [queryStr, setQueryStr] = useState({
        pageSize: QueryBuilderHelper.DefaultPageSizeValue(),
        pageNumber: QueryBuilderHelper.DefaultPageNumValue(),
        searchString: QueryBuilderHelper.DefaultSearchValue(),
        status: QueryBuilderHelper.DefaultQueryFilterValue('status') ?? 0,
        sortBy: QueryBuilderHelper.DefaultSortValue(DBInstancesListField.CREATED_DATE),
        orderBy: QueryBuilderHelper.DefaultOrderValue(OrderByEnum.DESC),
    });
    let [selectedRecord, setselectedRecord]: any = useState<DBInstancesList>(new DBInstancesList());
    const [openDelete, setopenDelete] = useState(false);

    const q_queryKey = [CachingEnum.DB_INSTANCES_LIST, queryStr]
    const { data, fetchStatus, instancesInvalidateQueries, downloadInstanceBackupById, restoreInstanceBackupById, deleteDBInstanceById } = useInstanceManagementHook(q_queryKey, queryStr, true, InstanceQueryTypeEnum.DATABASE_INSTANCES, instanceId);
    
    const tableColumns = [
        {
            accessor: DBInstancesListField.INSTANCE_NAME,
            title: 'Instance',
            sortable: true,
            render: (record: DBInstancesList) => {
                let sourceName = record?.[DBInstancesListField.SOURCE_NAME] != null && record?.[DBInstancesListField.SOURCE_NAME] != "" ? record?.[DBInstancesListField.SOURCE_NAME] : null;
                let createdByName = record?.[DBInstancesListField.CREATED_BY_NAME] != null && record?.[DBInstancesListField.CREATED_BY_NAME] != "" ? record?.[DBInstancesListField.CREATED_BY_NAME] : null;
                
                return <InstanceDetailsContainer $darkMode={isDarkMode}>
                    <span className='dbInstanceName'>{record?.[DBInstancesListField.INSTANCE_NAME] ?? '--'}</span>
                    <span className='cloneText'>{`cloned from ${sourceName ?? "--"} · by ${createdByName ?? "--"}`}</span>
                </InstanceDetailsContainer>
            }
        },
        {
            accessor: DBInstancesListField.STATUS,
            title: 'Status',
            sortable: true,
            render: (record: DBInstancesList) => {
                let status: DBInstancesStatusEnum = record?.[DBInstancesListField.STATUS] ?? DBInstancesStatusEnum.READY;
                let statusText = DBInstancesStatusTextEnum[status];
                
                return <InstanceDetailsContainer $darkMode={isDarkMode}>
                    <span className={'dbInstancesStatus ' + statusText.toLowerCase()}>{statusText}</span>
                </InstanceDetailsContainer>
            }
        },
        {
            accessor: DBInstancesListField.DATA_MODE,
            title: 'Data Mode',
            sortable: true,
            render: (record: DBInstancesList) => {
                let dataMode: InstanceDataModeEnum = record?.[DBInstancesListField.DATA_MODE] ?? InstanceDataModeEnum.FULL_COPY;
                let dataModeText = InstanceDataModeTextEnum[dataMode];
                
                return <InstanceDetailsContainer $darkMode={isDarkMode}>
                    <span className="alDateText">{dataModeText}</span>
                </InstanceDetailsContainer>
            }
        },
        {
            accessor: DBInstancesListField.SIZE_GB,
            title: 'Size',
            sortable: true,
            render: (record: DBInstancesList) => {
                 return <InstanceDetailsContainer $darkMode={isDarkMode}>
                    <span className="alDateText">{record?.[DBInstancesListField.SIZE_GB] ?? 0} GB</span>
                </InstanceDetailsContainer>
            }
        },
        {
            accessor: DBInstancesListField.CREATED_DATE,
            title: 'Created',
            sortable: true,
            render: (record: DBInstancesList) => {
                let createdDate: string | Date | null = record?.[DBInstancesListField.CREATED_DATE] != null && record?.[DBInstancesListField.CREATED_DATE] != "" ? DateHelper.getFormattedDate(record?.[DBInstancesListField.CREATED_DATE], "dd/MM/yyyy hh:mm a") : null;
                
                return <InstanceDetailsContainer $darkMode={isDarkMode}>
                    <span className='alDateText'>{createdDate ?? '--'}</span>
                </InstanceDetailsContainer>
            }
        },
        {
            accessor: DBInstancesListField.AUTO_DELETE_AT,
            title: 'Auto-Delete',
            sortable: true,
            render: (record: DBInstancesList) => {
                let autoDeleteAt: string | Date | null = record?.[DBInstancesListField.AUTO_DELETE_AT] != null && record?.[DBInstancesListField.AUTO_DELETE_AT] != "" ? DateHelper.getFormattedDate(record?.[DBInstancesListField.AUTO_DELETE_AT], "dd/MM/yyyy") : null;
                
                return <InstanceDetailsContainer $darkMode={isDarkMode}>
                    <span className='alDateText'>{autoDeleteAt ?? '--'}</span>
                </InstanceDetailsContainer>
            }
        },
        {
            accessor: '#',
            title: "Action",
            render: (record: DBInstancesList) => <>
                <CustomIconButton toolTipName='Copy connection string'
                    icon={<IconCopy />}
                    onClick={(e: any) => {
                        e.preventDefault()
        
                        copy(record?.[DBInstancesListField.INSTANCE_NAME] ?? "");
        
                        enqueueSnackbar(`Connection string for “${record?.[DBInstancesListField.INSTANCE_NAME] ?? ""}” copied`, { variant: 'success' });
                    }} />

                {(record?.[DBInstancesListField.STATUS] == DBInstancesStatusEnum.READY || record?.[DBInstancesListField.STATUS] == DBInstancesStatusEnum.FAILED) && <CustomIconButton toolTipName='Delete instance'
                    icon={<IconTrash color='var(--buttonHoverBGDangerColor)' />}
                    onClick={async (e: any) => {
                        setselectedRecord(record);
                        setopenDelete(true);
                    }} /> }
            </>
        },
    ];

    //Event Handlers
    useEffect(() => {
        instancesInvalidateQueries();
    }, [instanceId]);
    
    //Rendering Functions
    return <SPTableContainer>
        <TableContainer elevation={2} $isNotBorderRadiusAll={true}>
            <CustomListComponent
                fetching={(fetchStatus == "fetching" && triggeredQuery == CachingEnum.DB_INSTANCES_LIST) || (fetchStatus == "fetching" && triggeredQuery == 'all')}
                striped
                columns={tableColumns}
                idAccessor={(record: DBInstancesList) => `dbInstances${record?.[DBInstancesListField.PUBLIC_ID]}`}
                records={data?.data?.list ?? []}
                totalRecords={data?.data?.metaData?.totalCount ?? 0}
                page={queryStr.pageNumber}
                recordsPerPage={queryStr.pageSize}
                defaultColumnSort={{ columnAccessor: queryStr?.sortBy ?? 'id', direction: queryStr?.orderBy ?? 'asc' }}
                onSortStatusChange={((sort: any) => { 
                    if(settriggeredQuery) {
                        settriggeredQuery(CachingEnum.DB_INSTANCES_LIST);
                    }

                    setQueryStr((prev: any) => ({ ...prev, sortBy: sort?.columnAccessor ?? "id", orderBy: sort?.direction ?? "asc", pageNumber: 1 }));
                })}
                setPage={(p: number) => setQueryStr((prev: any) => ({ ...prev, pageNumber: p }))}
                onRecordsPerPageChange={(p: number) => setQueryStr((prev: any) => ({ ...prev, pageSize: p, pageNumber: 1 }))} />
        </TableContainer>

        {openDelete && <CustomDeleteConfirmation
            ItemToDelete={selectedRecord?.[DBInstancesListField.INSTANCE_NAME] ?? null}
            openDialog={openDelete}
            showRemarksField={true}
            handleClose={() => {
                setopenDelete(false);
                setselectedRecord(new DBInstancesList());
            }}
            deleteItem={async (remarks: any) => {
                if(instanceId != null && instanceId != "" && selectedRecord?.[DBInstancesListField.PUBLIC_ID] != null && selectedRecord?.[DBInstancesListField.PUBLIC_ID] != "") {
                    if(settriggeredQuery) {
                        settriggeredQuery(CachingEnum.DB_INSTANCES_LIST);
                    }

                    let deleteReponse: any = await deleteDBInstanceById(instanceId, selectedRecord?.[DBInstancesListField.PUBLIC_ID], remarks);

                    if(deleteReponse?.isSuccess) {
                        setopenDelete(false);
                        setselectedRecord(new DBInstancesList());
                    }
                }
            }} />
        }
    </SPTableContainer>
}
